import SideTitle from "@/components/SideTitle";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import "@cyntler/react-doc-viewer/dist/index.css";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import {
  COMM_TYPES_PARENT,
  COMM_TYPES_STUDENT,
  COMM_TYPES_SCHOOL,
  COMM_GROUPS,
  LEARNING_REQUIREMENTS,
  COMM_TYPES,
  COMM_TYPES_SYSTEM,
  SubmissionPayload,
  AGE_GROUPS,
} from "@/lib/types/comms";
import ResultPanel from "@/components/comm-generator/ResultPanel";
import SplitPane from "@/components/SplitPane";
import SpeechTextArea from "@/components/ui/speech-text-area";
import A11yOptions from "@/components/comm-generator/A11yOptions";
import CommunicationTypeSelector from "@/components/comm-generator/CommunicationTypeSelector";
import { CheckCircle, PaperPlaneTilt, Plus } from "@phosphor-icons/react";
import useUser from "@/lib/queries/user/useUser";
import useSessionStorage from "@/lib/hooks/useSessionStorage";
import { filterFormPreferences } from "@/lib/utils/filterFormPreferences";
import { useToolSubmissionSSE } from "@/lib/queries/tools/useToolSubmissionSSE";
import { useToolSubmission } from "@/lib/queries/tools/useToolSubmission";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStytchSession } from "@stytch/react";
import { cn } from "@/lib/utils";
import AppFormContainer from "@/components/AppFormContainer";
import { Button } from "@/components/ui/button";
import { useUserTemplates } from "@/lib/queries/user/useTemplates";
import { TemplateUploadModal } from "@/components/TemplateUploadModal";
import { Badge } from "@/components/ui/badge";
// useMemo imported above
import useInit from "@/lib/queries/user/useInit";
import { LanguageSelector } from "@/components/lesson-lab/LanguageSelector";
import { Trans, useTranslation } from "react-i18next";
import useUserSettings from "@/lib/queries/user/useUserSettings";
import { DEFAULT_RESULT_LANGUAGE } from "@/lib/constants/languages";
import { resolveInitialToolLanguage } from "@/lib/utils/language";
import { useA11ySettings } from "@/lib/hooks/useA11ySettings";
import TutorialBox, { useTutorialState } from "@/components/TutorialBox";
import { GuidedTour, TourStep } from "@/components/GuidedTour";
import FloatingDocStatuses from "@/components/results/FloatingDocStatuses";
import { buildDocStatuses } from "@/components/results/buildDocStatuses";
import { useIsMobile } from "@/hooks/use-mobile";

const getBadgeStyles = (category?: string, subcategory?: string) => {
  if (!category) return "text-gray-700 bg-gray-100";

  switch (category) {
    case "Resource":
      return "text-blue-800 bg-blue-100 border-blue-200";
    case "Communication":
      switch (subcategory) {
        case "Parents":
          return "text-green-800 bg-green-100 border-green-200";
        case "Students":
          return "text-purple-800 bg-purple-100 border-purple-200";
        case "School":
          return "text-orange-800 bg-orange-100 border-orange-200";
        case "Other":
          return "text-pink-800 bg-pink-100 border-pink-200";
        default:
          return "text-emerald-800 bg-emerald-100 border-emerald-200";
      }
    default:
      return "text-gray-700 bg-gray-100";
  }
};

function TabTrigger({
  value,
  onClick,
  children,
}: {
  value: COMM_GROUPS;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <TabsTrigger
      value={value}
      onClick={onClick}
      className="px-3 py-2 font-light transition-colors bg-white rounded-lg cursor-pointer whitespace-nowrap w-fit"
    >
      {children}
    </TabsTrigger>
  );
}

// Deprecated: legacy user.settings.language logic removed; using consistent resolver
// Priority: a11y override > resultLanguage > displayLanguage > default
function resolveInitialLanguage(
  userSettings: any | undefined,
  a11yLanguage?: string | null
) {
  return resolveInitialToolLanguage({
    a11yLanguage,
    userSettings,
    fallback: DEFAULT_RESULT_LANGUAGE,
  });
}

function CommunicationGenerator() {
  const { t } = useTranslation(["communicationGenerator", "common"]);
  const isMobile = useIsMobile();
  const { session, fromCache } = useStytchSession();
  const { data: user } = useUser({
    enabled: !!session && !fromCache,
  });
  const { useGetUserSettings } = useUserSettings();
  const { language: a11yLanguage } = useA11ySettings();

  const { data: userSettings } = useGetUserSettings({
    enabled: !!session && !fromCache,
  });

  const [storedIntermediate] = useState<string | null>(null);
  const [lastSubject, setLastSubject] = useState<string | null>(null);

  // Feature flag check for template functionality
  const { data: initData } = useInit();
  const isTemplateFeatureEnabled =
    initData?.features?.some(
      (feature) => feature.name === "Templates" && feature.active === true
    ) ?? false;

  // Fetch communication templates (only if feature is enabled)
  const { data: templatesData } = useUserTemplates("Communication", {
    enabled: isTemplateFeatureEnabled,
  });
  const [templateUploadModalOpen, setTemplateUploadModalOpen] = useState(false);

  const initialLanguage = useMemo(
    () => resolveInitialLanguage(userSettings, a11yLanguage),
    [userSettings, a11yLanguage]
  );
  const form = useForm<SubmissionPayload>({
    defaultValues: {
      type: COMM_TYPES_PARENT.INTRODUCTORY,
      recipient: COMM_GROUPS.PARENTS,
      from: `${user ? user?.settings?.firstName : "Teacher"} at Balamory Primary School`,
      subject: "",
      showAccessibilityOptions: "no",
      age: AGE_GROUPS.UNSET,
      learning_requirement: LEARNING_REQUIREMENTS.UNSET,
      language: initialLanguage,
      selectedTemplate: "__no_template__",
    },
  });

  const {
    watch,
    formState: { isDirty },
  } = form;

  // Watch only the fields we actually use to avoid rerendering on every input change
  const recipient = watch("recipient");
  const showAccessibilityOptions = watch("showAccessibilityOptions");
  const currentType = watch("type");

  // Get available templates for current selection
  const availableTemplates = useMemo(() => {
    if (!templatesData?.templates) return [];

    return templatesData.templates.filter((template) => {
      return template.tags.some((tag) => {
        // Check for general templates that apply to all
        if (
          tag.category === "communication" &&
          tag.general &&
          !tag.subcategory
        ) {
          return true;
        }

        // Check for subcategory general templates
        if (
          tag.category === "communication" &&
          tag.general &&
          tag.subcategory
        ) {
          const subcategoryMap = {
            [COMM_GROUPS.PARENTS]: "commGroups.parents",
            [COMM_GROUPS.STUDENTS]: "commGroups.students",
            [COMM_GROUPS.SCHOOL]: "commGroups.school",
            [COMM_GROUPS.SYSTEM]: "commGroups.system",
          };
          return tag.subcategory === subcategoryMap[recipient];
        }

        // Check for specific type matches
        if (tag.category === "communication" && tag.tagText === currentType) {
          return true;
        }

        return false;
      });
    });
  }, [templatesData?.templates, recipient, currentType]);

  // Automatically set default template when templates are available
  useEffect(() => {
    if (isTemplateFeatureEnabled && availableTemplates.length > 0) {
      const currentSelectedTemplate = form.getValues("selectedTemplate");
      if (currentSelectedTemplate === "__no_template__") {
        form.setValue("selectedTemplate", availableTemplates[0].id);
      }
    }
  }, [availableTemplates, isTemplateFeatureEnabled, form]);

  const USE_SSE = true;
  const sse = USE_SSE
    ? useToolSubmissionSSE("communicationsgenerator")
    : (null as any);
  const mutation = !USE_SSE
    ? useToolSubmission("communicationsgenerator")
    : (null as any);

  const onSubmit = form.handleSubmit(
    async ({
      type,
      recipient,
      subject,
      learning_requirement,
      age,
      language,
      selectedTemplate,
    }) => {
      // Handle template selection - convert special value to undefined
      const templateId =
        selectedTemplate === "__no_template__" || !selectedTemplate
          ? undefined
          : selectedTemplate;

      const subjectChanged = lastSubject !== null && lastSubject !== subject;
      const intermediateValue = subjectChanged ? null : storedIntermediate;

      const payload = {
        subject,
        attachments: [],
        type: [
          {
            name: t(type, {
              lng: "en",
              ns: "communicationGenerator",
            }),
            recipient: t(recipient, {
              lng: "en",
              ns: "communicationGenerator",
            }),
            duration: 0,
            durationUnit: undefined,
            templateId: templateId,
          },
        ],
        requirements: {
          age:
            age === AGE_GROUPS.UNSET
              ? null
              : t(age, {
                  lng: "en",
                  ns: "common",
                }),
          language:
            t(language || "english", {
              lng: "en",
              ns: "common",
            }) || "english",
          learning_requirement:
            learning_requirement === LEARNING_REQUIREMENTS.UNSET
              ? null
              : t(learning_requirement, {
                  lng: "en",
                  ns: "common",
                }),
        },
        regenerate: {
          intermediate: intermediateValue || null,
          feedback: null,
        },
      } as const;

      if (USE_SSE) {
        sse.start(payload as any);
      } else {
        (mutation as any).mutate(payload as any);
      }

      setLastSubject(subject);
    }
  );

  // Save preferences functionality
  const [savePreferencesEnabled, setSavePreferencesEnabled] = useState(false);
  const [getPreferences, setPreferences] = useSessionStorage(
    "communication-generator-preferences",
    {}
  );

  useEffect(() => {
    const subscription = watch((value) => {
      if (!value) return;
      if (isDirty && savePreferencesEnabled) {
        const preferences = filterFormPreferences(value, ["from"]);
        setPreferences(preferences);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, isDirty, savePreferencesEnabled, setPreferences]);

  useEffect(() => {
    if (getPreferences && Object.keys(getPreferences).length > 0) {
      form.reset({
        ...form.getValues(),
        ...getPreferences,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update language when a11y or user settings change (respect user modification)
  useEffect(() => {
    const desired = resolveInitialLanguage(userSettings, a11yLanguage);
    const current = form.getValues("language");
    const languageDirty = (form.formState.dirtyFields as any)?.language;
    if (!languageDirty && current !== desired) {
      form.setValue("language", desired, { shouldDirty: false });
    }
  }, [a11yLanguage, userSettings, form, initialLanguage]);

  // With SSE we don't currently receive a separate 'intermediate' payload; keep any prior value
  // so regenerate can include it when appropriate.

  const [tourOpen, setTourOpen] = useState(false);
  const tutorialState = useTutorialState("commgen-tutorial");
  const tourSteps: TourStep[] = useMemo(
    () => [
      {
        selector: "#side-title",
        title: t("tutorial.1.title"),
        content: t("tutorial.1.body", {
          ns: "communicationGenerator",
        }),
      },
      {
        selector: "textarea[name=subject]",
        title: t("tutorial.2.title"),
        content: t("tutorial.2.body", {
          ns: "communicationGenerator",
        }),
      },
      {
        selector: "#comm-who",
        title: t("tutorial.3.title"),
        content: t("tutorial.3.body", {
          ns: "communicationGenerator",
        }),
      },
      {
        selector: "#tour-language-container",
        title: t("tutorial.4.title"),
        content: t("tutorial.4.body", {
          ns: "communicationGenerator",
        }),
      },
      {
        selector: "#tour-generate",
        title: t("tutorial.5.title"),
        content: t("tutorial.5.body", {
          ns: "communicationGenerator",
        }),
      },
    ],
    [t]
  );

  // Memoize recipient tabs to avoid re-rendering the full Tabs tree on unrelated input changes
  const recipientTabs = useMemo(
    () => (
      <Tabs defaultValue={COMM_GROUPS.PARENTS}>
        <TabsList className="flex flex-row flex-wrap items-start gap-2 mb-5 bg-transparent justify-stretch h-fit w-fit">
          <FormField
            control={form.control}
            name="recipient"
            render={({ field }) => (
              <>
                <TabTrigger
                  value={COMM_GROUPS.PARENTS}
                  onClick={() => {
                    form.setValue("type", COMM_TYPES_PARENT.INTRODUCTORY);
                    field.onChange(COMM_GROUPS.PARENTS);
                  }}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center mr-2 transition-[width] duration-200 ease-in-out",
                      recipient === COMM_GROUPS.PARENTS
                        ? "opacity-100 w-5 h-5"
                        : "opacity-0 w-0 h-0 mr-0"
                    )}
                  >
                    <CheckCircle weight="bold" className="font-bold" />
                  </span>
                  {t("commGroups.parents")}
                </TabTrigger>
                <TabTrigger
                  value={COMM_GROUPS.STUDENTS}
                  onClick={() => {
                    form.setValue("type", COMM_TYPES_STUDENT.BEHAVIOUR_REPORT);
                    field.onChange(COMM_GROUPS.STUDENTS);
                  }}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center mr-2 transition-[width] duration-200 ease-in-out",
                      recipient === COMM_GROUPS.STUDENTS
                        ? "opacity-100 w-5 h-5"
                        : "opacity-0 w-0 h-0 mr-0"
                    )}
                  >
                    <CheckCircle weight="bold" className="font-bold" />
                  </span>
                  {t("commGroups.students")}
                </TabTrigger>
                <TabTrigger
                  value={COMM_GROUPS.SCHOOL}
                  onClick={() => {
                    form.setValue("type", COMM_TYPES_SCHOOL.BEHAVIOUR_REPORT);
                    field.onChange(COMM_GROUPS.SCHOOL);
                  }}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center mr-2 transition-[width] duration-200 ease-in-out",
                      recipient === COMM_GROUPS.SCHOOL
                        ? "opacity-100 w-5 h-5"
                        : "opacity-0 w-0 h-0 mr-0"
                    )}
                  >
                    <CheckCircle weight="bold" className="font-bold" />
                  </span>
                  {t("commGroups.school")}
                </TabTrigger>
                <TabTrigger
                  value={COMM_GROUPS.SYSTEM}
                  onClick={() => {
                    form.setValue("type", COMM_TYPES_SYSTEM.SEEMIS);
                    field.onChange(COMM_GROUPS.SYSTEM);
                  }}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center mr-2 transition-[width] duration-200 ease-in-out",
                      recipient === COMM_GROUPS.SYSTEM
                        ? "opacity-100 w-5 h-5"
                        : "opacity-0 w-0 h-0 mr-0"
                    )}
                  >
                    <CheckCircle weight="bold" className="font-bold" />
                  </span>
                  {t("commGroups.system")}
                </TabTrigger>
              </>
            )}
          />
        </TabsList>
        <FormLabel className="font-semibold">{t("commTypes.label")}</FormLabel>
        <TabsContent value={COMM_GROUPS.PARENTS}>
          <CommunicationTypeSelector
            groupTypes={COMM_TYPES}
            currentGroup={COMM_GROUPS.PARENTS}
            otherType={COMM_TYPES_PARENT.OTHER}
          />
        </TabsContent>
        <TabsContent value={COMM_GROUPS.STUDENTS}>
          <CommunicationTypeSelector
            groupTypes={COMM_TYPES}
            currentGroup={COMM_GROUPS.STUDENTS}
            otherType={COMM_TYPES_STUDENT.OTHER}
          />
        </TabsContent>
        <TabsContent value={COMM_GROUPS.SCHOOL}>
          <CommunicationTypeSelector
            groupTypes={COMM_TYPES}
            currentGroup={COMM_GROUPS.SCHOOL}
            otherType={COMM_TYPES_SCHOOL.OTHER}
          />
        </TabsContent>
        <TabsContent value={COMM_GROUPS.SYSTEM}>
          <CommunicationTypeSelector
            groupTypes={COMM_TYPES}
            currentGroup={COMM_GROUPS.SYSTEM}
            otherType={COMM_TYPES_SYSTEM.OTHER}
          />
        </TabsContent>
      </Tabs>
    ),
    [form, recipient, t]
  );

  return (
    <>
      <SplitPane
        showResultPanel={
          USE_SSE
            ? Boolean(sse.final || sse.latestOutput)
            : Boolean(mutation?.data)
        }
        key={sse.final || sse.latestOutput ? "comm-result" : "comm-form"}
      >
        <AppFormContainer
          form={form}
          onSubmit={onSubmit}
          isSuccess={Boolean(sse.final)}
          isPending={sse.isStreaming}
          savePreferences={{
            enabled: savePreferencesEnabled,
            onToggle: setSavePreferencesEnabled,
          }}
        >
          <SideTitle
            intro={t("header.intro")}
            title={t("header.title")}
            icon={PaperPlaneTilt}
            showHelpIndicator={tutorialState.dismissed}
            onHelpClick={tutorialState.reopen}
          />
          <TutorialBox
            storageKey="commgen-tutorial"
            title={t("common:quickGuide", { defaultValue: "Quick guide" })}
            message={
              t("tutorial.blurb", {
                ns: "communicationGenerator",
                defaultValue: "Quick tour of the Communication Generator.",
              }) || "Quick tour of the Communication Generator."
            }
            onStartTour={() => setTourOpen(true)}
            tutorialState={tutorialState}
          />
          <GuidedTour
            open={tourOpen}
            steps={tourSteps}
            storageKey="commgen-tutorial"
            onClose={() => setTourOpen(false)}
          />
          {user?.trialUsed && user?.creditsRemaining > 0 && (
            <Alert variant={"warning"} className="mt-4">
              <AlertTitle>{t("common:trialWarning.title")}</AlertTitle>
              <AlertDescription className="mt-2 text-foreground">
                <Trans
                  i18nKey={"common:trialWarning.description"}
                  components={{
                    1: <strong />,
                  }}
                  values={{
                    creditsRemaining: user?.creditsRemaining,
                  }}
                ></Trans>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col">
            <div className="flex flex-col mb-4">
              <FormLabel htmlFor="subject" className="mb-4 font-semibold">
                {t("speechTextArea.label")}
              </FormLabel>

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <SpeechTextArea
                    {...field}
                    placeholder={[
                      t("speechTextArea.placeholder.1"),
                      t("speechTextArea.placeholder.2"),
                      t("speechTextArea.placeholder.3"),
                      t("speechTextArea.placeholder.4"),
                      t("speechTextArea.placeholder.5"),
                    ]}
                  />
                )}
              />
            </div>
            <div id="comm-who">
              <FormLabel className="font-semibold">
                {t("commGroups.label")}
              </FormLabel>
              <FormDescription className="text-content !text-light-grey mb-4">
                {t("commGroups.description")}
              </FormDescription>
              {recipientTabs}
            </div>
            {/* Template Selection */}
            {isTemplateFeatureEnabled &&
              (availableTemplates.length > 0 ? (
                <div className="mt-6">
                  <FormLabel className="flex items-center gap-2 font-semibold">
                    {t("templates.label")}
                    <Badge
                      variant="outline"
                      className="text-purple-800 bg-purple-100 border-purple-200"
                    >
                      {t("templates.available", {
                        count: availableTemplates.length,
                      })}
                    </Badge>
                  </FormLabel>
                  <FormDescription className="text-content !text-light-grey mb-4">
                    {t("templates.description")}
                  </FormDescription>
                  <FormField
                    control={form.control}
                    name="selectedTemplate"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue
                                placeholder={t("templates.placeholder")}
                              />
                            </SelectTrigger>{" "}
                            <SelectContent>
                              <SelectItem value="__no_template__">
                                {t("templates.noTemplate")}
                              </SelectItem>
                              {availableTemplates.map((template) => {
                                const tagsPreview = template.tags.slice(0, 2);
                                const extraCount = template.tags.length - 2;
                                return (
                                  <SelectItem
                                    key={template.id}
                                    value={template.id}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>{template.name}</span>
                                      <div className="flex gap-1">
                                        {tagsPreview.map((tag) => (
                                          <Badge
                                            key={tag.id}
                                            variant="outline"
                                            className={`${getBadgeStyles(tag.category, tag.subcategory)} text-xs`}
                                          >
                                            {tag.tagText}
                                          </Badge>
                                        ))}
                                        {extraCount > 0 && (
                                          <Badge
                                            variant="outline"
                                            className="text-xs"
                                          >
                                            +{extraCount}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              ) : (
                <div className="mt-6">
                  <FormLabel className="font-semibold">
                    {t("templates.noAvailable.label")}
                  </FormLabel>
                  <FormDescription className="text-content !text-light-grey mb-4">
                    {t("templates.noAvailable.description")}
                  </FormDescription>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setTemplateUploadModalOpen(true)}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    {t("templates.uploadButton")}
                  </Button>
                </div>
              ))}

            <LanguageSelector name="language" />

            <FormField
              control={form.control}
              name="showAccessibilityOptions"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between mt-2">
                  <FormLabel className="">
                    {recipient === "commGroups.parents" ||
                    recipient === "commGroups.students"
                      ? t("accessibility.label", { recipient: t(recipient) })
                      : t("accessibility.labelGeneral")}
                  </FormLabel>
                  <FormControl>
                    <Switch
                      className="!mt-0"
                      checked={showAccessibilityOptions === "yes"}
                      onCheckedChange={(checked) =>
                        field.onChange(checked ? "yes" : "no")
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {showAccessibilityOptions === "yes" && <A11yOptions />}
          </div>
        </AppFormContainer>
        <ResultPanel
          result={
            USE_SSE
              ? sse.final
                ? sse.final.results
                : sse.latestOutput
                  ? [sse.latestOutput]
                  : undefined
              : mutation?.data?.results
          }
          appResult="communication-generator"
          isLoading={USE_SSE ? sse.isStreaming : Boolean(mutation?.isPending)}
          toolResponse={USE_SSE ? sse.final : mutation?.data}
          sseEvents={USE_SSE ? sse.events : undefined}
          sseError={USE_SSE ? sse.error : (mutation?.error as any)}
          loadingStatuses={
            USE_SSE
              ? buildDocStatuses({
                  expectedDocs: [
                    {
                      type: t(watch("type"), {
                        lng: "en",
                        ns: "communicationGenerator",
                      }),
                      language:
                        t(
                          form.getValues("language") || DEFAULT_RESULT_LANGUAGE,
                          { lng: "en", ns: "common" }
                        ) || "english",
                    },
                  ],
                  sseEvents: sse.events,
                  results:
                    (sse.final?.results as any) ||
                    (sse.latestOutput ? [sse.latestOutput] : []),
                  isStreaming: sse.isStreaming,
                  isLoading: sse.isStreaming,
                })
              : undefined
          }
          loadingStage={USE_SSE ? sse.stage : undefined}
          loadingProgress={USE_SSE ? sse.progress : undefined}
          onCancel={USE_SSE ? sse.abort : undefined}
        />
      </SplitPane>

      {USE_SSE &&
        (() => {
          const docStatuses = buildDocStatuses({
            expectedDocs: [
              {
                type: t(watch("type"), {
                  lng: "en",
                  ns: "communicationGenerator",
                }),
                language:
                  t(form.getValues("language") || DEFAULT_RESULT_LANGUAGE, {
                    lng: "en",
                    ns: "common",
                  }) || "english",
              },
            ],
            sseEvents: sse.events,
            results:
              (sse.final?.results as any) ||
              (sse.latestOutput ? [sse.latestOutput] : []),
            isStreaming: sse.isStreaming,
            isLoading: sse.isStreaming,
          });
          const hasReady = docStatuses.some((s) => s.status === "ready");
          const showFloating =
            Boolean(sse.isStreaming) &&
            (isMobile || hasReady) &&
            docStatuses.length > 0 &&
            sse.stage !== "done" &&
            !sse.error;
          return (
            <FloatingDocStatuses
              visible={showFloating}
              statuses={docStatuses}
              stage={sse.stage}
              progress={sse.progress}
              onClose={() => {}}
              onCancel={sse.abort}
            />
          );
        })()}

      {isTemplateFeatureEnabled && (
        <TemplateUploadModal
          open={templateUploadModalOpen}
          onOpenChange={setTemplateUploadModalOpen}
          onSuccess={() => {
            // Refetch templates when a new one is uploaded
            // The query will automatically update due to the key change
            setTemplateUploadModalOpen(false);
          }}
        />
      )}
    </>
  );
}

export default CommunicationGenerator;
